// This service will encapsulate all upload-related operations.
// It will interact with the backend to initiate uploads, manage chunked uploads,
// dispatch actions to update UploadContext, implement retry logic, and handle resumability.

import { UploadTask, UploadAction } from "@/contexts/UploadContext"
import { User } from "firebase/auth"
import React from "react" // React might not be directly needed, but dispatch is a React type

const VITE_API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

/**
 * Represents the structure of the response from the initiate upload endpoint.
 */
interface InitiateUploadResponse {
	session_uri: string
	// Add other relevant fields from the backend response if any
}

/**
 * Represents the structure of the response from the complete upload endpoint.
 */
interface CompleteUploadResponse {
	message: string
	// Add other relevant fields from the backend response if any
}

// Store for active XHR requests to allow cancellation
// Key: taskId, Value: XMLHttpRequest object
const activeXhrRequests: Record<string, XMLHttpRequest> = {}

/**
 * `uploadService` provides a centralized set of functions to manage the entire file upload lifecycle.
 * This includes initiating uploads with the backend, performing chunked uploads to Google Cloud Storage (GCS),
 * handling retries, managing resumability, and dispatching state updates to the global `UploadContext`.
 *
 * **Key Responsibilities:**
 * - **Initiate Upload:**
 *   - Contact the backend to get a GCS resumable session URI for a new file.
 *   - Dispatch `SET_SESSION_URI` to `UploadContext`.
 * - **Perform Chunked Upload:**
 *   - Upload the file in chunks to the GCS `sessionUri`.
 *   - Dispatch `UPDATE_TASK_PROGRESS` to `UploadContext` regularly.
 *   - Handle GCS responses (e.g., 308 for ongoing, 200/201 for completion).
 * - **Complete Upload:**
 *   - Notify the backend once GCS confirms the file is fully uploaded.
 *   - Dispatch `UPDATE_TASK_STATUS` (to 'completed') to `UploadContext`.
 * - **Error Handling & Retries:**
 *   - Implement retry logic for transient network errors or server issues (e.g., exponential backoff).
 *   - Dispatch `UPDATE_TASK_STATUS` (to 'error') with an error message if retries fail.
 * - **Resumability:**
 *   - Query GCS for the status of an existing `sessionUri` to determine `uploadedBytes`.
 *   - Resume uploading from the last known `uploadedBytes`.
 * - **Cancellation:**
 *   - Provide a mechanism to abort ongoing `XMLHttpRequest` or `fetch` requests for uploads.
 *   - (This part would be called when `REMOVE_TASK` is dispatched for an active task).
 * - **State Management:**
 *   - Accept `dispatchUploadAction` from `UploadContext` to update global state.
 *   - Interact with `UploadTask` objects from the context.
 */
export const uploadService = {
	/**
	 * Initiates a new upload session with the backend and GCS.
	 *
	 * @async
	 * @param task - The upload task from `UploadContext` (should have `file`, `caseId`).
	 * @param user - The authenticated Firebase user.
	 * @param dispatchUploadAction - The dispatch function from `UploadContext`.
	 * @returns A Promise that resolves when the initiation is complete or rejects on error.
	 */
	initiateUpload: async (
		task: UploadTask,
		user: User,
		dispatch: React.Dispatch<UploadAction>
	): Promise<void> => {
		if (!task.file || !task.caseId || !user) {
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: task.id,
					status: "error",
					error: "Missing file, case ID, or user for initiation.",
				},
			})
			return
		}
		try {
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: { id: task.id, status: "uploading", error: undefined },
			})
			dispatch({
				type: "UPDATE_TASK_PROGRESS",
				payload: { id: task.id, progress: 1, uploadedBytes: 0 },
			})

			const token = await user.getIdToken()
			const initiateResponse = await fetch(
				`${VITE_API_URL}/api/cases/${task.caseId}/upload-session/`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						filename: task.fileName,
						content_type: task.fileType || "application/octet-stream",
					}),
				}
			)

			if (!initiateResponse.ok) {
				const errorData = await initiateResponse.json().catch(() => ({}))
				throw new Error(
					`Failed to initiate upload: ${
						errorData.detail || initiateResponse.statusText
					}`
				)
			}

			const { session_uri: sessionUri } =
				(await initiateResponse.json()) as InitiateUploadResponse
			dispatch({
				type: "SET_SESSION_URI",
				payload: { id: task.id, sessionUri },
			})
			dispatch({
				type: "UPDATE_TASK_PROGRESS",
				payload: { id: task.id, progress: 5, uploadedBytes: 0 },
			})

			// Pass the updated task (with sessionUri) to performResumableUpload
			const updatedTask = { ...task, sessionUri }
			await uploadService.performResumableUpload(updatedTask, user, dispatch)
		} catch (error: any) {
			console.error("Error initiating upload:", error)
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: task.id,
					status: "error",
					error: error.message || "Initiation failed.",
				},
			})
		}
	},

	performResumableUpload: async (
		task: UploadTask, // Changed to accept the full task object
		user: User, // For completing the upload
		dispatch: React.Dispatch<UploadAction>,
		retryCount = 0
	): Promise<void> => {
		if (!task.file || !task.sessionUri) {
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: task.id,
					status: "error",
					error: "File or session URI missing for upload.",
				},
			})
			return
		}

		const { id: taskId, file, sessionUri, fileSize } = task
		let { uploadedBytes } = task // Get initial uploadedBytes from the task

		const maxRetries = 3

		try {
			// If resuming, first query GCS for actual uploaded bytes to be certain
			if (task.status === "pending_resume" || uploadedBytes > 0) {
				try {
					const gcsUploadedBytes = await uploadService.queryUploadedBytes(
						sessionUri,
						fileSize,
						dispatch,
						taskId
					)
					if (gcsUploadedBytes !== uploadedBytes) {
						console.log(
							`GCS reports ${gcsUploadedBytes} bytes for task ${taskId}, local state was ${uploadedBytes}. Updating.`
						)
						uploadedBytes = gcsUploadedBytes
						dispatch({
							type: "UPDATE_TASK_PROGRESS",
							payload: {
								id: taskId,
								progress: Math.round((uploadedBytes / fileSize) * 85) + 5,
								uploadedBytes,
							},
						})
					} else if (gcsUploadedBytes < uploadedBytes) {
						// This is unusual, might indicate an issue or a new session for the same file name.
						// For now, trust local if it's higher, but log a warning.
						console.warn(
							`Local uploadedBytes (${uploadedBytes}) for task ${taskId} is greater than GCS reported (${gcsUploadedBytes}). Proceeding with local value.`
						)
					}
				} catch (queryError: any) {
					// If query fails, we might proceed with local uploadedBytes but it's risky
					console.error(
						`Failed to query GCS for task ${taskId}, proceeding with potentially stale uploadedBytes (${uploadedBytes}): ${queryError.message}`
					)
					// Potentially set status to error here if query is critical for resume
				}
			}

			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: { id: taskId, status: "uploading" },
			}) // Ensure status is uploading
			dispatch({
				type: "UPDATE_TASK_PROGRESS",
				payload: {
					id: taskId,
					progress: Math.round((uploadedBytes / fileSize) * 85) + 5,
					uploadedBytes,
				},
			})

			const xhr = new XMLHttpRequest()
			activeXhrRequests[taskId] = xhr

			// XMLHttpRequest onload (success or final chunk)
			xhr.onload = async () => {
				delete activeXhrRequests[taskId]
				if (xhr.status === 200 || xhr.status === 201) {
					// Final chunk uploaded successfully, file complete on GCS
					dispatch({
						type: "UPDATE_TASK_PROGRESS",
						payload: { id: taskId, progress: 90, uploadedBytes: fileSize },
					})
					await uploadService.completeUpload(task, user, dispatch)
				} else if (xhr.status === 308) {
					// Chunk uploaded, but not the last one
					// This status typically means GCS is waiting for more data.
					// The 'while' loop should handle sending the next chunk.
					// This block might not be strictly necessary if the loop correctly updates progress and continues.
				} else {
					// Other non-successful statuses
					dispatch({
						type: "UPDATE_TASK_STATUS",
						payload: {
							id: taskId,
							status: "error",
							error: `Upload failed: ${xhr.status} ${xhr.statusText}`,
						},
					})
				}
			}

			// XMLHttpRequest onerror
			xhr.onerror = () => {
				delete activeXhrRequests[taskId]
				if (retryCount < maxRetries) {
					const delay = Math.pow(2, retryCount) * 1000
					console.log(
						`Network error on task ${taskId}. Retrying in ${delay}ms (attempt ${
							retryCount + 1
						})`
					)
					setTimeout(
						() =>
							uploadService.performResumableUpload(
								task,
								user,
								dispatch,
								retryCount + 1
							),
						delay
					)
				} else {
					dispatch({
						type: "UPDATE_TASK_STATUS",
						payload: {
							id: taskId,
							status: "error",
							error: "Network error during upload after multiple retries.",
						},
					})
				}
			}

			// XMLHttpRequest onabort
			xhr.onabort = () => {
				delete activeXhrRequests[taskId]
				console.log(`Upload aborted for task ${taskId}`)
				// Status is usually set to 'paused' or removed by the UI action that triggered abort.
				// If not, we can set it to error or a specific 'aborted' status.
				// dispatch({ type: "UPDATE_TASK_STATUS", payload: { id: taskId, status: "error", error: "Upload aborted by user." } });
			}

			// XMLHttpRequest onprogress (for detailed progress of the current chunk if needed)
			// Store the bytes uploaded before this chunk started
			const bytesUploadedBeforeThisChunk = uploadedBytes
			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable) {
					const currentChunkProgress = event.loaded
					const totalBytesUploaded = bytesUploadedBeforeThisChunk + currentChunkProgress
					const overallProgress =
						Math.round(
							(totalBytesUploaded / fileSize) * 85
						) + 5
					dispatch({
						type: "UPDATE_TASK_PROGRESS",
						payload: {
							id: taskId,
							progress: overallProgress,
							uploadedBytes: totalBytesUploaded,
						},
					})
				}
			}

			if (!activeXhrRequests[taskId]) {
				// Check if upload was aborted externally
				console.log(
					`Upload for task ${taskId} was aborted before sending next chunk.`
				)
				return
			}

			xhr.open("PUT", sessionUri, true)
			xhr.setRequestHeader(
				"Content-Range",
				`bytes ${uploadedBytes}-${fileSize - 1}/${fileSize}`
			)
			// Content-Type is usually not needed for GCS PUT on session URI for chunks after the first one.
			// xhr.setRequestHeader("Content-Type", task.fileType || "application/octet-stream");

			// Using a promise to await XHR completion for each chunk
			await new Promise<void>((resolve, reject) => {
				const tempOnLoad = xhr.onload
				const tempOnError = xhr.onerror
				xhr.onload = () => {
					if (xhr.status === 200 || xhr.status === 201 || xhr.status === 308) {
						if (xhr.status === 200 || xhr.status === 201) {
							// File complete
							uploadedBytes = fileSize
						} else {
							// status 308, check Range header from GCS
							const rangeHeader = xhr.getResponseHeader("Range")
							if (rangeHeader) {
								const match = rangeHeader.match(/bytes=0-(\d+)/)
								if (match) {
									uploadedBytes = parseInt(match[1], 10) + 1
								} else {
									// If range header is not as expected, it's safer to assume current chunk uploaded
									uploadedBytes = fileSize
								}
							} else {
								uploadedBytes = fileSize // Fallback if no Range header
							}
						}
						dispatch({
							type: "UPDATE_TASK_PROGRESS",
							payload: {
								id: taskId,
								progress: Math.round((uploadedBytes / fileSize) * 85) + 5,
								uploadedBytes,
							},
						})
						if (tempOnLoad) tempOnLoad.apply(xhr) // Call original onload for final completion logic
						resolve()
					} else {
						if (tempOnError) tempOnError.apply(xhr) // Call original error handler
						reject(
							new Error(`Chunk upload failed: ${xhr.status} ${xhr.statusText}`)
						)
					}
				}
				xhr.onerror = () => {
					if (tempOnError) tempOnError.apply(xhr)
					reject(new Error("Network error during chunk upload."))
				}
				xhr.send(file.slice(uploadedBytes, fileSize))
			})
		} catch (error: any) {
			delete activeXhrRequests[taskId]
			console.error(`Error during resumable upload for task ${taskId}:`, error)
			// Retry logic is now within xhr.onerror for network issues
			// This catch block handles other unexpected errors or if retries were exhausted by chunk promise rejection
			if (
				retryCount < maxRetries &&
				(error.message.includes("network") ||
					error.message.includes("Chunk upload failed"))
			) {
				const delay = Math.pow(2, retryCount) * 1000
				console.log(
					`Retrying upload for task ${taskId} from outer catch in ${delay}ms (attempt ${
						retryCount + 1
					})`
				)
				await new Promise((resolve) => setTimeout(resolve, delay))
				return uploadService.performResumableUpload(
					task,
					user,
					dispatch,
					retryCount + 1
				)
			}
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: taskId,
					status: "error",
					error: error.message || "Upload failed during chunking.",
				},
			})
		}
	},

	completeUpload: async (
		task: UploadTask,
		user: User,
		dispatch: React.Dispatch<UploadAction>
	): Promise<void> => {
		const { id: taskId, fileName, fileSize, caseId, sessionUri } = task
		if (!caseId) {
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: taskId,
					status: "error",
					error: "Case ID missing for completion.",
				},
			})
			return
		}

		try {
			dispatch({
				type: "UPDATE_TASK_PROGRESS",
				payload: { id: taskId, progress: 95, uploadedBytes: fileSize },
			})
			const token = await user.getIdToken(true)

			const completeResponse = await fetch(
				`${VITE_API_URL}/api/cases/${caseId}/upload/complete/`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						filename: fileName,
						file_size: fileSize,
						session_uri: sessionUri, // Pass sessionUri for backend to potentially use/verify
					}),
				}
			)

			if (!completeResponse.ok) {
				const errorData = await completeResponse.json().catch(() => ({}))
				throw new Error(
					`Failed to complete upload with backend: ${
						errorData.detail || completeResponse.statusText
					}`
				)
			}

			await completeResponse.json()
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: { id: taskId, status: "completed" },
			})
			dispatch({
				type: "UPDATE_TASK_PROGRESS",
				payload: { id: taskId, progress: 100, uploadedBytes: fileSize },
			})
		} catch (error: any) {
			console.error("Error completing upload with backend:", error)
			dispatch({
				type: "UPDATE_TASK_STATUS",
				payload: {
					id: taskId,
					status: "error",
					error: error.message || "Backend completion failed.",
				},
			})
		}
	},

	queryUploadedBytes: async (
		sessionUri: string,
		fileSize: number,
		dispatch: React.Dispatch<UploadAction>,
		taskId: string
	): Promise<number> => {
		const statusResponse = await fetch(sessionUri, {
			method: "PUT",
			headers: {
				"Content-Length": "0",
				"Content-Range": `bytes */${fileSize}`,
			},
		})

		if (statusResponse.status === 308) {
			const rangeHeader = statusResponse.headers.get("Range")
			if (rangeHeader) {
				const match = rangeHeader.match(/bytes=0-(\d+)/)
				if (match) {
					return parseInt(match[1], 10) + 1
				}
			}
			return 0
		} else if (statusResponse.status === 200 || statusResponse.status === 201) {
			return fileSize
		} else {
			const errorText = await statusResponse
				.text()
				.catch(() => "Unknown GCS error querying session.")
			console.error(
				`GCS session query failed for ${sessionUri} (task: ${taskId}) with status ${statusResponse.status}: ${errorText}`
			)
			throw new Error(`GCS session query failed: ${statusResponse.statusText}`)
		}
	},

	abortUpload: async (
		task: UploadTask,
		dispatch: React.Dispatch<UploadAction>
	): Promise<void> => {
		const { id: taskId, sessionUri } = task

		// 1. Attempt to cancel GCS session if URI exists
		if (sessionUri) {
			try {
				const response = await fetch(sessionUri, { method: "DELETE" })
				// HTTP 204 No Content is a common success response for DELETE.
				// HTTP 404 Not Found or 410 Gone indicate the session is already inactive or invalid.
				if (response.ok || response.status === 404 || response.status === 410) {
					console.log(
						`GCS session cancellation for task ${taskId} reported: ${response.status}`
					)
				} else {
					const errorText = await response
						.text()
						.catch(() => "Failed to read error response.")
					console.warn(
						`Failed to cancel GCS resumable upload session for task ${taskId}. Status: ${response.status} ${response.statusText}. Server message: ${errorText}`
					)
				}
			} catch (error: any) {
				// Catch network errors or other issues during the fetch itself
				console.warn(
					`Network error or other issue during GCS session cancellation request for task ${taskId}: ${error.message}`
				)
			}
		}

		// 2. Abort client-side XHR if active
		if (activeXhrRequests[taskId]) {
			activeXhrRequests[taskId].abort()
			delete activeXhrRequests[taskId]
			console.log(`Client-side XHR for task ${taskId} aborted.`)
		} else {
			console.log(
				`No active client-side XHR found for task ${taskId} to abort (it might have already completed, failed, or not started).`
			)
		}

		// 3. Dispatch update to context to mark task as aborted
		dispatch({
			type: "UPDATE_TASK_STATUS",
			payload: {
				id: taskId,
				status: "aborted",
				error: "Upload cancelled by user.", // Consistent message
			},
		})
	},
}
